import React, { useEffect, useRef, useState } from 'react';
import { cameraMotionService, POSE_CONNECTIONS } from '../services/cameraMotionService';
import { CameraTrackingState, SESSION_DURATION_PRESETS } from '../types';
import { SessionDurationPicker } from './SessionDurationPicker';

interface CameraMotionHUDProps {
  isActive: boolean;
  onToggleActive: () => void;
  accentColor?: string;
  isMobile?: boolean;
  sessionDuration?: number;
  onChangeDuration?: (seconds: number) => void;
  sessionElapsed?: number;
  isFadingOut?: boolean;
  isRecording?: boolean;
  isArmingRecord?: boolean;
  onToggleArmRecord?: () => void;
  isSessionRunning?: boolean;
  onStartSession?: (opts?: { record?: boolean }) => void;
  onTriggerRecord?: () => void;
  onStopSession?: () => void;
  isZenMode?: boolean;
  onOpenMotionGuide?: () => void;
  isMinimized?: boolean;
  onToggleMinimized?: (minimized: boolean) => void;
}

export const CameraMotionHUD: React.FC<CameraMotionHUDProps> = ({
  isActive,
  onToggleActive,
  accentColor = '#38bdf8',
  isMobile = false,
  sessionDuration = 0,
  onChangeDuration,
  sessionElapsed = 0,
  isFadingOut = false,
  isRecording = false,
  isArmingRecord = false,
  onToggleArmRecord,
  isSessionRunning = false,
  onStartSession,
  onTriggerRecord,
  onStopSession,
  isZenMode = false,
  onOpenMotionGuide,
  isMinimized: isMinimizedProp,
  onToggleMinimized
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [trackingState, setTrackingState] = useState<CameraTrackingState>(cameraMotionService.getState());
  const [isMinimizedInternal, setIsMinimizedInternal] = useState(false);
  const isMinimized = isMinimizedProp !== undefined ? isMinimizedProp : isMinimizedInternal;
  const toggleMinimized = (val: boolean) => {
    setIsMinimizedInternal(val);
    if (onToggleMinimized) onToggleMinimized(val);
  };
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [armRecordLocal, setArmRecordLocal] = useState(isArmingRecord);

  // Keep local record arm in sync with parent prop
  useEffect(() => {
    setArmRecordLocal(isArmingRecord);
  }, [isArmingRecord]);

  // Subscribe to tracking updates
  useEffect(() => {
    const unsub = cameraMotionService.subscribeState((state) => {
      setTrackingState(state);
      if (state.modelError) {
        setPermissionError(state.modelError);
      } else {
        setPermissionError(null);
      }
    });
    return unsub;
  }, []);

  // Handle active camera lifecycle
  useEffect(() => {
    if (isActive) {
      const start = async () => {
        setPermissionError(null);
        if (videoRef.current) {
          const success = await cameraMotionService.startCamera(videoRef.current, 'user', false);
          if (!success) {
            setPermissionError('Could not access webcam. Please check camera permissions in your browser.');
          }
        }
      };
      start();
    } else {
      cameraMotionService.stopCamera();
    }

    return () => {
      cameraMotionService.stopCamera();
    };
  }, [isActive]);

  // Render skeleton on canvas overlay
  useEffect(() => {
    if (!isActive || isMinimized) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;

    const renderCanvas = () => {
      const state = cameraMotionService.getState();
      const width = canvas.width;
      const height = canvas.height;

      ctx.clearRect(0, 0, width, height);

      if (state.isStreaming && state.hasDetectedBody && state.landmarks.length > 0) {
        const lms = state.landmarks;

        // Draw connections (skeleton bones)
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = accentColor;
        ctx.shadowColor = accentColor;
        ctx.shadowBlur = 10;

        for (const [i, j] of POSE_CONNECTIONS) {
          const p1 = lms[i];
          const p2 = lms[j];
          if (p1 && p2 && (p1.visibility ?? 1) > 0.3 && (p2.visibility ?? 1) > 0.3) {
            ctx.beginPath();
            ctx.moveTo(p1.x * width, p1.y * height);
            ctx.lineTo(p2.x * width, p2.y * height);
            ctx.stroke();
          }
        }

        // Draw key joints
        for (let idx = 0; idx < lms.length; idx++) {
          const lm = lms[idx];
          if (!lm || (lm.visibility ?? 1) <= 0.3) continue;

          // Special highlight for wrists and hands (indices 15, 16, 19, 20)
          const isHand = idx === 15 || idx === 16 || idx === 19 || idx === 20;
          const isHead = idx === 0;

          ctx.beginPath();
          const radius = isHand ? 6.5 : (isHead ? 5 : 3.5);
          ctx.arc(lm.x * width, lm.y * height, radius, 0, Math.PI * 2);

          if (isHand) {
            ctx.fillStyle = '#ffffff';
            ctx.shadowBlur = 15;
            ctx.shadowColor = '#60a5fa';
            ctx.fill();
            ctx.lineWidth = 2;
            ctx.strokeStyle = accentColor;
            ctx.stroke();

            // Dynamic pulse ring around active playing hands
            const speed = (idx === 15 || idx === 19) 
              ? (state.leftHand?.speed || 0) 
              : (state.rightHand?.speed || 0);
            
            const pulseRadius = radius + Math.min(18, speed * 6);
            ctx.beginPath();
            ctx.arc(lm.x * width, lm.y * height, pulseRadius, 0, Math.PI * 2);
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
            ctx.lineWidth = 1;
            ctx.stroke();
          } else {
            ctx.fillStyle = accentColor;
            ctx.shadowBlur = 6;
            ctx.shadowColor = accentColor;
            ctx.fill();
          }
        }
      }

      animId = requestAnimationFrame(renderCanvas);
    };

    renderCanvas();
    return () => cancelAnimationFrame(animId);
  }, [isActive, isMinimized, accentColor]);

  const formatSeconds = (secs: number) => {
    const s = Math.max(0, Math.floor(secs));
    const mins = Math.floor(s / 60);
    const rem = s % 60;
    return `${mins.toString().padStart(2, '0')}:${rem.toString().padStart(2, '0')}`;
  };

  const remainingSeconds = sessionDuration > 0 ? Math.max(0, sessionDuration - sessionElapsed) : 0;
  const progressPercent = sessionDuration > 0
    ? Math.min(100, Math.max(0, (sessionElapsed / sessionDuration) * 100))
    : 0;

  if (!isActive) return null;

  return (
    <div 
      id="camera-motion-hud" 
      className={`fixed z-40 transition-all duration-300 ${
        isZenMode ? 'opacity-30 hover:opacity-100' : 'opacity-100'
      } ${
        isMinimized 
          ? 'top-3 sm:top-6 right-2 sm:right-6 w-auto' 
          : 'top-3 sm:top-6 right-2 sm:right-6 left-2 sm:left-auto w-auto sm:w-88 md:w-96 max-w-[calc(100vw-1rem)]'
      }`}
    >
      <div className="bg-slate-950/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden flex flex-col text-white max-h-[calc(100dvh-6.5rem)] sm:max-h-[calc(100dvh-7.5rem)] overflow-y-auto">
        {/* Header Bar */}
        <div className="flex items-center justify-between gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-2 sm:py-2.5 bg-slate-900/90 border-b border-white/10 shrink-0 min-w-0 flex-wrap">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
              trackingState.hasDetectedBody 
                ? 'bg-emerald-400 animate-pulse shadow-sm shadow-emerald-400' 
                : (trackingState.isLoadingModel ? 'bg-amber-400 animate-spin' : 'bg-red-400')
            }`} />
            <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-200 truncate">
              Webcam
            </span>
            {trackingState.isStreaming && (
              <span className="text-[10px] sm:text-xs bg-indigo-500/20 text-indigo-300 font-mono px-1.5 py-0.5 rounded border border-indigo-500/30 shrink-0 whitespace-nowrap">
                {trackingState.fps} FPS
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {/* Live Recording Status Badge in Header */}
            {isRecording && onTriggerRecord && (
              <button
                type="button"
                onClick={onTriggerRecord}
                className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1 rounded-full bg-red-600 hover:bg-red-500 text-white text-[10px] sm:text-[11px] font-bold animate-pulse shadow-md border border-red-400/50 cursor-pointer shrink-0 min-h-[32px]"
                title="Recording Audio • Click to Stop & Save"
                aria-label="Recording Audio • Click to Stop & Save"
              >
                <span className="w-2 h-2 rounded-full bg-white" />
                <span>REC</span>
              </button>
            )}

            {/* Quick Motion Map Guide Button */}
            {onOpenMotionGuide && (
              <button
                onClick={onOpenMotionGuide}
                className="p-1.5 text-sky-400 hover:text-sky-300 rounded-lg hover:bg-sky-500/10 transition shrink-0 min-h-[32px] min-w-[32px] flex items-center justify-center"
                title="View Motion Mapping Guide"
                aria-label="View Motion Mapping Guide"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
                </svg>
              </button>
            )}

            {/* Flip camera on mobile */}
            <button
              onClick={() => cameraMotionService.switchFacingMode()}
              className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition shrink-0 min-h-[32px] min-w-[32px] flex items-center justify-center"
              title="Switch Camera (Front/Rear)"
              aria-label="Switch Camera"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
            </button>

            {/* Minimize / Maximize */}
            <button
              onClick={() => toggleMinimized(!isMinimized)}
              className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition shrink-0 min-h-[32px] min-w-[32px] flex items-center justify-center"
              title={isMinimized ? "Expand Preview" : "Minimize Preview"}
              aria-label={isMinimized ? "Expand Preview" : "Minimize Preview"}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                {isMinimized ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15" />
                )}
              </svg>
            </button>

            {/* Close Camera */}
            <button
              onClick={onToggleActive}
              className="p-1.5 text-slate-400 hover:text-red-400 rounded-lg hover:bg-red-500/10 transition shrink-0 min-h-[32px] min-w-[32px] flex items-center justify-center"
              title="Turn Off Camera"
              aria-label="Turn Off Camera"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Video & Skeleton Overlay Canvas - Keep mounted in DOM so tracking & video streams never break */}
        <div className={`relative w-full aspect-video bg-black/90 flex items-center justify-center overflow-hidden shrink-0 ${isMinimized ? 'hidden' : 'block'}`}>
          <video
            ref={videoRef}
            className="w-full h-full object-cover -scale-x-100"
            playsInline
            muted
            autoPlay
          />

          <canvas
            ref={canvasRef}
            width={320}
            height={240}
            className="absolute inset-0 w-full h-full object-cover -scale-x-100 pointer-events-none"
          />

          {/* Loading or Status Badges */}
          {trackingState.isLoadingModel && (
            <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm flex flex-col items-center justify-center p-4 text-center">
              <div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin mb-2"></div>
              <span className="text-xs font-semibold text-indigo-200">Loading Pose Vision Model...</span>
              <span className="text-[10px] text-slate-400 mt-1">Initializing AI tracking engine</span>
            </div>
          )}

          {permissionError && (
            <div className="absolute inset-0 bg-slate-950/95 flex flex-col items-center justify-center p-4 text-center z-20">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center mb-2.5">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-amber-400">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                </svg>
              </div>
              <span className="text-xs text-amber-200 font-semibold mb-1">Camera Permission Blocked</span>
              <p className="text-[11px] text-slate-300 max-w-[260px] leading-relaxed mb-3">
                {permissionError}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setPermissionError(null);
                    if (videoRef.current) cameraMotionService.startCamera(videoRef.current);
                  }}
                  className="text-xs bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-3 py-1.5 rounded-lg transition active:scale-95 shadow-md shadow-indigo-600/30"
                >
                  Retry Access
                </button>
                <button
                  onClick={onToggleActive}
                  className="text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold px-3 py-1.5 rounded-lg transition border border-white/10"
                >
                  Use Touch / Cursor
                </button>
              </div>
            </div>
          )}

          {/* Tracking Guideline Overlay */}
          {trackingState.isStreaming && !trackingState.hasDetectedBody && !trackingState.isLoadingModel && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-slate-950/40 p-2">
              <div className="border border-dashed border-indigo-400/50 rounded-xl px-3 py-1.5 bg-slate-900/80 text-[11px] text-indigo-300 animate-pulse text-center">
                Step in view & raise hands to play
              </div>
            </div>
          )}

          {/* Dynamic Status Badges on Camera Preview */}
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-[10px] font-mono pointer-events-none gap-1">
            <span className={`px-2 py-0.5 rounded backdrop-blur-md border truncate ${
              isRecording 
                ? 'bg-red-950/80 text-red-300 border-red-500/40 animate-pulse'
                : trackingState.hasDetectedBody 
                  ? 'bg-slate-900/80 text-emerald-300 border-emerald-500/30'
                  : 'bg-slate-900/80 text-amber-300 border-amber-500/30'
            }`}>
              {isRecording ? '🔴 Recording Audio' : trackingState.hasDetectedBody ? '🖐️ Tracking Hands' : 'Stand in View'}
            </span>
            <span className="bg-slate-900/80 backdrop-blur-md px-2 py-0.5 rounded text-indigo-300 border border-indigo-500/30 truncate">
              Motion: {(trackingState.velocity * 100).toFixed(0)}%
            </span>
          </div>
        </div>

        {/* HUD Controls & Sleek Collapsible Drawer */}
        <div className={`p-3 bg-slate-950 border-t border-white/5 space-y-2.5 shrink-0 ${isMinimized ? 'hidden' : 'block'}`}>
          {!isSessionRunning ? (
            /* PRE-SESSION SETUP VIEW: Choose timer, toggle record, then start session & countdown */
            <div className="space-y-3 animate-fade-in">
              <div className="flex items-center justify-between gap-2 border-b border-white/10 pb-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-200 uppercase tracking-wider">
                  <span>⏱️</span>
                  <span>Session Setup</span>
                </div>
                <span className="text-[10px] text-sky-400 font-mono font-bold bg-sky-950/60 border border-sky-500/30 px-2 py-0.5 rounded-full">
                  {sessionDuration === 0 ? '♾️ Free Play' : `${Math.round(sessionDuration / 60)} min`}
                </span>
              </div>

              {/* Timer Duration Picker */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-slate-300 block">
                  1. Choose Practice Duration
                </label>
                {onChangeDuration && (
                  <SessionDurationPicker
                    selectedDuration={sessionDuration}
                    onSelectDuration={(sec) => onChangeDuration(sec)}
                    compact
                  />
                )}
              </div>

              {/* Record Session Toggle */}
              <div className="bg-slate-900/90 border border-white/10 rounded-xl p-2.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <div className={`w-3 h-3 rounded-full shrink-0 ${armRecordLocal ? 'bg-red-500 animate-pulse shadow-sm shadow-red-500' : 'bg-slate-600'}`} />
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-slate-200 truncate">Record Session Audio</div>
                    <div className="text-[10px] text-slate-400 leading-tight truncate">Save sound & reflection to Sound Library</div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    const next = !armRecordLocal;
                    setArmRecordLocal(next);
                    if (onToggleArmRecord) onToggleArmRecord();
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition active:scale-95 border shrink-0 ${
                    armRecordLocal
                      ? 'bg-red-600/30 text-red-300 border-red-500/60 shadow-inner'
                      : 'bg-slate-800 hover:bg-slate-700 text-slate-300 border-white/10 hover:text-white'
                  }`}
                >
                  {armRecordLocal ? '✓ Armed' : 'Record: OFF'}
                </button>
              </div>

              {/* Primary Start Session CTA Button */}
              <button
                type="button"
                onClick={() => {
                  if (onStartSession) {
                    onStartSession({ record: armRecordLocal });
                  }
                }}
                className={`w-full py-2.5 px-4 rounded-xl font-black text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all active:scale-95 shadow-xl cursor-pointer ${
                  armRecordLocal
                    ? 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white shadow-red-600/30 border border-red-400/40'
                    : 'bg-gradient-to-r from-indigo-600 to-sky-600 hover:from-indigo-500 hover:to-sky-500 text-white shadow-indigo-600/30 border border-indigo-400/40'
                }`}
              >
                {armRecordLocal ? (
                  <>
                    <span className="w-2.5 h-2.5 rounded-full bg-white animate-ping shrink-0" />
                    <span>Start & Record Session</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 shrink-0">
                      <path fillRule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z" clipRule="evenodd" />
                    </svg>
                    <span>Start Movement Session</span>
                  </>
                )}
              </button>
            </div>
          ) : (
            /* ACTIVE PERFORMANCE VIEW: Real-time telemetry, session timer, recording control, and stop button */
            <div className="space-y-2.5 animate-fade-in">
              {/* Quick Metrics Grid */}
              <div className="grid grid-cols-2 gap-2 text-[11px] w-full">
                <div className="bg-slate-900/80 p-2 rounded-xl border border-white/5 flex flex-col min-w-0">
                  <span className="text-slate-400 text-[10px] truncate">Pitch & Melodies</span>
                  <span className="font-bold text-indigo-300 truncate">
                    {trackingState.leftHand || trackingState.rightHand ? 'Hand Position' : 'Raise Hands'}
                  </span>
                </div>
                <div className="bg-slate-900/80 p-2 rounded-xl border border-white/5 flex flex-col min-w-0">
                  <span className="text-slate-400 text-[10px] truncate">Tone & Brightness</span>
                  <span className="font-bold text-teal-300 truncate">
                    {trackingState.velocity > 0.6 ? 'High Energy' : 'Gentle Ambient'}
                  </span>
                </div>
              </div>

              {/* Active Session Progress & Fadeout Status */}
              {sessionDuration > 0 ? (
                <div className="bg-slate-900/90 border border-white/10 rounded-xl p-2.5 space-y-1.5 w-full min-w-0">
                  <div className="flex items-center justify-between text-[11px] font-mono gap-2 min-w-0">
                    <span className="text-slate-400 text-[10px] uppercase font-bold tracking-wider font-sans truncate">
                      {isFadingOut ? 'Fading Out...' : 'Session Time'}
                    </span>
                    <span className="text-sky-300 font-bold whitespace-nowrap">
                      {formatSeconds(remainingSeconds)} <span className="text-slate-500 font-normal text-[10px]">left</span>
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden shrink-0">
                    <div 
                      className={`h-full transition-all duration-300 ${isFadingOut ? 'bg-amber-400 animate-pulse' : 'bg-sky-400'}`}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-slate-900/90 border border-white/10 rounded-xl px-2.5 py-1.5 flex items-center justify-between text-[11px] font-mono">
                  <span className="text-slate-400 text-[10px]">Free Flow Mode</span>
                  <span className="text-emerald-400 font-bold">{formatSeconds(sessionElapsed)} elapsed</span>
                </div>
              )}

              {/* Quick Record & Fade-Stop Controls */}
              <div className="flex items-center gap-2 pt-0.5">
                {onTriggerRecord && (
                  <button
                    type="button"
                    onClick={onTriggerRecord}
                    className={`flex-1 py-2 px-3 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition active:scale-95 border ${
                      isRecording
                        ? 'bg-red-600 hover:bg-red-500 text-white border-red-400 animate-pulse shadow-lg shadow-red-500/20'
                        : 'bg-slate-900 hover:bg-slate-800 text-slate-200 border-white/10 hover:text-white'
                    }`}
                    title={isRecording ? "Stop & Save Live Recording" : "Record Live Audio Capture"}
                  >
                    <span className={`w-2.5 h-2.5 rounded-full ${isRecording ? 'bg-white' : 'bg-red-500'}`} />
                    <span>{isRecording ? 'Finish & Save Audio' : 'Record Audio'}</span>
                  </button>
                )}

                {onStopSession && (
                  <button
                    type="button"
                    onClick={onStopSession}
                    disabled={isFadingOut}
                    className="py-2 px-3 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl border border-white/10 text-xs font-semibold transition active:scale-95 shrink-0"
                    title="Fade out audio and return to setup"
                  >
                    ■ End Early
                  </button>
                )}
              </div>

              {/* Collapsible Session Settings Drawer Header / Trigger */}
              <div className="pt-0.5 w-full">
                <button
                  type="button"
                  onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-xs font-semibold border transition-all text-left min-w-0 ${
                    isDrawerOpen
                      ? 'bg-sky-950/60 border-sky-500/50 text-sky-200 shadow-inner'
                      : 'bg-slate-900/90 hover:bg-slate-900 border-white/10 text-slate-300 hover:text-white'
                  }`}
                  title="Adjust timer length during session"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="text-sm shrink-0">⏱️</span>
                    <span className="font-bold truncate">Adjust Timer</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-sky-300 font-mono font-bold border border-white/10 shrink-0 whitespace-nowrap">
                      {sessionDuration === 0 ? 'Free Play' : `${Math.round(sessionDuration / 60)}m`}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5 text-slate-400 shrink-0">
                    <span className="text-[10px] hidden sm:inline">{isDrawerOpen ? 'Hide' : 'Change'}</span>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      strokeWidth={2.5} 
                      stroke="currentColor" 
                      className={`w-3.5 h-3.5 transition-transform duration-200 ${isDrawerOpen ? 'rotate-180 text-sky-300' : ''}`}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                    </svg>
                  </div>
                </button>

                {/* Expanded Collapsible Drawer Panel */}
                {isDrawerOpen && onChangeDuration && (
                  <div className="mt-2 p-3 bg-slate-900/95 border border-sky-500/30 rounded-xl space-y-2 animate-fade-in shadow-inner w-full min-w-0">
                    <SessionDurationPicker
                      selectedDuration={sessionDuration}
                      onSelectDuration={(sec) => {
                        onChangeDuration(sec);
                      }}
                      compact
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Clean Screen-Centered Countdown Overlay (Only shows once on camera launch) */}
      {trackingState.isCountdownActive && trackingState.countdown !== null && (
        <div 
          id="camera-countdown-overlay"
          className="fixed inset-0 z-[120] bg-slate-950/85 backdrop-blur-md flex flex-col items-center justify-center p-6 text-center animate-fade-in pointer-events-auto"
        >
          <div className="max-w-md w-full flex flex-col items-center">
            {/* Animated Countdown Ring */}
            <div className="relative w-32 h-32 sm:w-36 sm:h-36 flex items-center justify-center mb-5">
              <div 
                className="absolute inset-0 rounded-full border-2 border-sky-400/30 animate-ping opacity-60" 
                style={{ animationDuration: '1s' }}
              />
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-slate-900/95 border-2 border-sky-400 shadow-2xl shadow-sky-500/40 flex items-center justify-center">
                <span 
                  key={trackingState.countdown}
                  className={`font-black font-mono tracking-tighter animate-scale-in ${
                    trackingState.countdown === 0 
                      ? 'text-3xl sm:text-4xl text-emerald-400' 
                      : 'text-4xl sm:text-5xl text-sky-300'
                  }`}
                >
                  {trackingState.countdown === 0 ? 'Ready!' : trackingState.countdown}
                </span>
              </div>
            </div>

            {/* Instruction Headline */}
            <h2 className="text-xl sm:text-2xl font-black text-white tracking-wide mb-2">
              {trackingState.countdown === 0 ? 'Starting Music Flow' : 'Position Your Body & Hands'}
            </h2>

            <p className="text-xs sm:text-sm text-slate-300 max-w-xs sm:max-w-sm mb-6 leading-relaxed">
              Step back slightly so your upper body and hands are visible. Move your hands freely to sculpt ambient melodies.
            </p>

            {/* Live Camera Framing Status Indicator */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/90 border border-white/15 mb-5 text-xs font-semibold">
              <span className={`w-2.5 h-2.5 rounded-full ${
                trackingState.hasDetectedBody 
                  ? 'bg-emerald-400 animate-pulse shadow-sm shadow-emerald-400' 
                  : 'bg-amber-400'
              }`} />
              <span className={trackingState.hasDetectedBody ? 'text-emerald-300' : 'text-slate-300'}>
                {trackingState.hasDetectedBody 
                  ? 'Body & Hands In View • Ready' 
                  : 'Checking camera view...'}
              </span>
            </div>

            {/* Instant Skip Button */}
            <button
              onClick={() => cameraMotionService.skipCountdown()}
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full text-xs font-bold shadow-lg shadow-indigo-600/30 transition-all active:scale-95 flex items-center gap-2 cursor-pointer"
            >
              <span>Start Now (Skip)</span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
